/**
 * NFC Kartınız — interactive section behaviours.
 * Each behaviour is a custom element so sections stay declarative and the
 * theme editor can re-render a section without leaking listeners.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------------------
 * <nfc-before-after> — draggable before/after image comparison
 * ------------------------------------------------------------------------ */
class NfcBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('[data-nfc-ba-frame]');
    this.range = this.querySelector('[data-nfc-ba-range]');
    if (!this.frame || !this.range) return;

    this.onInput = () => this.setPosition(Number(this.range.value));
    this.range.addEventListener('input', this.onInput);

    this.onPointer = (event) => {
      if (event.buttons !== 1 && event.type !== 'pointerdown') return;
      const rect = this.frame.getBoundingClientRect();
      if (!rect.width) return;
      const pct = ((event.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.min(100, Math.max(0, pct));
      this.range.value = String(clamped);
      this.setPosition(clamped);
    };
    this.frame.addEventListener('pointerdown', this.onPointer);
    this.frame.addEventListener('pointermove', this.onPointer);

    this.setPosition(Number(this.range.value));
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.onInput);
    this.frame?.removeEventListener('pointerdown', this.onPointer);
    this.frame?.removeEventListener('pointermove', this.onPointer);
  }

  setPosition(value) {
    this.style.setProperty('--nfc-ba-position', `${value}%`);
    this.range.setAttribute('aria-valuenow', String(Math.round(value)));
  }
}

/* ---------------------------------------------------------------------------
 * <nfc-carousel> — auto-scrolling, pause-on-hover use-case rail
 * ------------------------------------------------------------------------ */
class NfcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-nfc-carousel-track]');
    if (!this.track) return;

    this.querySelectorAll('[data-nfc-carousel-nudge]').forEach((button) => {
      button.addEventListener('click', () => {
        const dir = button.dataset.nfcCarouselNudge === 'next' ? 1 : -1;
        this.track.scrollBy({ left: dir * 320, behavior: 'smooth' });
      });
    });

    if (reduceMotion()) return;

    // The markup duplicates the item list, so resetting at the halfway point loops seamlessly.
    this.paused = false;
    this.onEnter = () => { this.paused = true; };
    this.onLeave = () => { this.paused = false; };
    this.addEventListener('pointerenter', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);

    const tick = () => {
      if (!this.paused && this.track.scrollWidth > 0) {
        this.track.scrollLeft += 0.55;
        if (this.track.scrollLeft >= this.track.scrollWidth / 2) {
          this.track.scrollLeft -= this.track.scrollWidth / 2;
        }
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  disconnectedCallback() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onEnter);
    this.removeEventListener('focusout', this.onLeave);
  }
}

/* ---------------------------------------------------------------------------
 * <nfc-reveal> — adds .is-visible once scrolled into view
 * ------------------------------------------------------------------------ */
class NfcReveal extends HTMLElement {
  connectedCallback() {
    if (reduceMotion() || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.classList.add('is-visible');
          this.observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    this.observer.observe(this);
    // Safety net if the element never crosses the threshold (e.g. very tall viewport).
    this.timer = setTimeout(() => this.classList.add('is-visible'), 1600);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.timer);
  }
}

/* ---------------------------------------------------------------------------
 * <nfc-contact-bubble> — floating contact panel
 * ------------------------------------------------------------------------ */
class NfcContactBubble extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-nfc-bubble-toggle]');
    this.panel = this.querySelector('[data-nfc-bubble-panel]');
    if (!this.toggle || !this.panel) return;

    this.onToggle = () => this.setOpen(this.panel.hidden);
    this.toggle.addEventListener('click', this.onToggle);

    this.onClose = () => this.setOpen(false);
    this.querySelector('[data-nfc-bubble-close]')?.addEventListener('click', this.onClose);

    this.onKey = (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) {
        this.setOpen(false);
        this.toggle.focus();
      }
    };
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    document.removeEventListener('keydown', this.onKey);
  }

  setOpen(open) {
    this.panel.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.classList.toggle('is-open', open);
  }
}

/* ---------------------------------------------------------------------------
 * <nfc-accordion> — FAQ disclosure list
 * ------------------------------------------------------------------------ */
class NfcAccordion extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('details'));
    if (!this.items.length) return;
    this.onToggle = (event) => {
      if (!event.target.open) return;
      // Single-open behaviour matches the Accordion component in the design system.
      this.items.forEach((item) => {
        if (item !== event.target) item.open = false;
      });
    };
    this.items.forEach((item) => item.addEventListener('toggle', this.onToggle));
  }

  disconnectedCallback() {
    this.items?.forEach((item) => item.removeEventListener('toggle', this.onToggle));
  }
}

const registry = {
  'nfc-before-after': NfcBeforeAfter,
  'nfc-carousel': NfcCarousel,
  'nfc-reveal': NfcReveal,
  'nfc-contact-bubble': NfcContactBubble,
  'nfc-accordion': NfcAccordion,
};

Object.entries(registry).forEach(([tag, ctor]) => {
  if (!customElements.get(tag)) customElements.define(tag, ctor);
});

/* ---------------------------------------------------------------------------
 * .nfc-tabs — simple tablist, progressive enhancement over the hidden panels
 * ------------------------------------------------------------------------ */
document.addEventListener('click', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('.nfc-tabs__tab') : null;
  if (!tab) return;
  const root = tab.closest('.nfc-tabs');
  if (!root) return;

  root.querySelectorAll('.nfc-tabs__tab').forEach((t) => {
    const on = t === tab;
    t.classList.toggle('is-active', on);
    t.setAttribute('aria-selected', String(on));
    const panel = root.querySelector(`#${CSS.escape(t.getAttribute('aria-controls') || '')}`);
    if (panel) panel.hidden = !on;
  });
});

/* ---------------------------------------------------------------------------
 * Quantity ⇄ price ladder ⇄ Toplam card
 *
 * Horizon morphs only the variant picker on selection and leaves every other
 * component to re-render itself from shopify:product:select. These blocks read
 * the selected variant straight from the picker's checked radios and repaint
 * from a server-precomputed price map, so the ladder and the total follow the
 * variant immediately — before the network round-trip completes.
 *
 * The displayed total is presentational. Shopify prices the cart server-side,
 * so the ladder only matches what is charged if the same breaks exist as
 * automatic discounts in the admin.
 * ------------------------------------------------------------------------ */
const nfcScope = (el) => el.closest('.product-details') || document;

/** Price map rendered by the volume block, keyed by variant id. */
const nfcPriceData = (scope) => {
  const node = (scope || document).querySelector('[data-nfc-price-data]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || '{}');
  } catch {
    return null;
  }
};

/** Current variant, resolved from the picker's checked radios. */
const nfcSelectedVariant = (scope, data) => {
  if (!data?.variants) return null;

  const chosen = new Map();
  scope.querySelectorAll('.variant-picker__form input[type="radio"]:checked').forEach((input) => {
    const name = input.dataset.optionName;
    if (name) chosen.set(name, input.value);
  });

  if (chosen.size) {
    const key = (data.options || []).map((name) => chosen.get(name) ?? '').join('||');
    const hit = Object.entries(data.variants).find(([, v]) => v.key === key);
    if (hit) return hit[1];
  }

  // No radios yet (dropdown style, or first paint) — fall back to the id the
  // Toplam card was rendered with.
  const total = scope.querySelector('[data-nfc-total]');
  const id = total?.dataset.variantId;
  return id ? data.variants[id] ?? null : null;
};

/** Highest tier the chosen quantity reaches. */
const nfcActiveTier = (tiers, qty) => {
  let best = null;
  tiers.forEach((tier) => {
    const step = Number(tier.dataset.nfcTier);
    if (Number.isFinite(step) && qty >= step && (!best || step > Number(best.dataset.nfcTier))) {
      best = tier;
    }
  });
  return best;
};

const nfcSync = (root, qtyOverride) => {
  const scope = nfcScope(root);
  const data = nfcPriceData(scope);
  const variant = nfcSelectedVariant(scope, data);

  const select = scope.querySelector('[data-nfc-qty]');
  const qty = qtyOverride ?? Number(select?.value) ?? 1;
  const discountsLive = Boolean(data?.configured) && nfcDiscountsWork();

  // Repaint each tier at the selected variant's price.
  const tiers = Array.from(scope.querySelectorAll('[data-nfc-tier]'));
  tiers.forEach((tier) => {
    const step = String(Number(tier.dataset.nfcTier));
    const priced = variant?.tiers?.[step];
    if (!priced) return;

    const unitEl = tier.querySelector('.nfc-vol__unit');
    if (unitEl) unitEl.textContent = `${priced.unit} / adet`;

    const saveEl = tier.querySelector('.nfc-vol__save');
    if (saveEl) {
      const show = priced.hasSave && discountsLive;
      saveEl.textContent = show ? `${priced.save} tasarruf` : '—';
      saveEl.classList.toggle('nfc-vol__save--on', show);
    }
    tier.dataset.tierUnit = String(priced.off);
  });

  const active = nfcActiveTier(tiers, qty);
  tiers.forEach((tier) => {
    const on = tier === active;
    tier.classList.toggle('is-active', on);
    tier.setAttribute('aria-pressed', String(on));
  });

  if (select && Number(select.value) !== qty) {
    const match = Array.from(select.options).find((o) => Number(o.value) === qty);
    if (match) select.value = String(qty);
  }

  scope.querySelectorAll('[data-nfc-qty-target]').forEach((input) => {
    input.value = String(qty);
  });

  // The H1 lives in a text block that Horizon does not re-render on selection.
  scope.querySelectorAll('[data-nfc-variant-title]').forEach((el) => {
    if (variant?.title) el.textContent = variant.title;
  });

  // Repaint the Toplam card at variant × quantity × tier rate.
  const total = scope.querySelector('[data-nfc-total]');
  if (!total) return;
  const priceEl = total.querySelector('[data-nfc-total-price]');
  const summed = variant?.totals?.[String(qty)];

  if (priceEl && summed) {
    // `plain` is unit × qty with no tier applied — what Shopify will actually
    // charge when no discount object exists. Showing the discounted figure
    // then is what made the product page and the cart disagree.
    priceEl.textContent = discountsLive ? summed.total : summed.plain ?? summed.total;
  }

  const note = total.querySelector('[data-nfc-total-discount]');
  if (note) {
    // Only claim a discount when one can actually be applied to the cart, so
    // the product page and the cart can never contradict each other.
    const off = discountsLive ? summed?.off ?? 0 : 0;
    note.textContent = off > 0 ? `%${off} adet indirimi uygulandı` : '';
    note.hidden = off <= 0;
  }
};

/* ---------------------------------------------------------------------------
 * Tier discounts against the real cart
 *
 * A price ladder drawn in the theme is only a quote — Shopify prices the cart
 * server-side. Horizon applies codes with POST /cart/update.js {discount}, so
 * the qualifying tier's code is applied there and then read back. If the code
 * does not exist or does not apply, the claim is withdrawn for the session
 * rather than left showing a discount the customer will not be charged.
 * ------------------------------------------------------------------------ */
const NFC_DISCOUNT_BROKEN = 'nfc:tier-discount-unavailable';

const nfcDiscountsWork = () => {
  try {
    return sessionStorage.getItem(NFC_DISCOUNT_BROKEN) !== '1';
  } catch {
    return true;
  }
};

const nfcMarkDiscountsBroken = () => {
  try {
    sessionStorage.setItem(NFC_DISCOUNT_BROKEN, '1');
  } catch {
    /* private mode — fall through, the cart stays the source of truth */
  }
  document.querySelectorAll('[data-nfc-total-discount]').forEach((el) => {
    el.textContent = '';
    el.hidden = true;
  });
};

/** Apply the code for the tier the cart's quantity qualifies for. */
const nfcApplyTierDiscount = async () => {
  const data = nfcPriceData(document);
  if (!data || !data.configured) return;

  let cart;
  try {
    cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
  } catch {
    return;
  }

  const ids = new Set(Object.keys(data.variants));
  const qty = (cart.items || [])
    .filter((line) => ids.has(String(line.variant_id)))
    .reduce((sum, line) => sum + line.quantity, 0);
  if (!qty) return;

  // Every tier code we own, so a stale one can be dropped when the tier changes.
  const anyVariant = Object.values(data.variants)[0];
  const ourCodes = new Set(
    Object.values(anyVariant?.tiers || {})
      .map((t) => t.code)
      .filter(Boolean)
  );

  const variant = data.variants[String((cart.items || [])[0]?.variant_id)] || anyVariant;
  const wanted = variant?.totals?.[String(qty)]?.code || nfcBestTierCode(variant, qty);
  if (!wanted && !ourCodes.size) return;

  const existing = (cart.discount_codes || []).map((d) => d.code);
  const keep = existing.filter((c) => !ourCodes.has(c));
  const next = wanted ? [...keep, wanted] : keep;
  if (next.join(',') === existing.join(',')) return;

  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(',') }),
    });
    const updated = await res.json();
    const applied = (updated.discount_codes || []).find((d) => d.code === wanted);
    if (wanted && (!applied || applied.applicable === false)) {
      console.warn(`[nfc] discount code "${wanted}" is not applicable — create it in admin.`);
      nfcMarkDiscountsBroken();
    }
  } catch {
    /* leave the cart as the server rendered it */
  }
};

/** Highest tier at or below the quantity, when the exact quantity has no entry. */
const nfcBestTierCode = (variant, qty) => {
  let best = null;
  Object.entries(variant?.tiers || {}).forEach(([step, tier]) => {
    if (qty >= Number(step) && tier.code && (!best || Number(step) > best.step)) {
      best = { step: Number(step), code: tier.code };
    }
  });
  return best?.code || '';
};

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('[data-nfc-qty]')) {
    nfcSync(target, Number(/** @type {HTMLSelectElement} */ (target).value) || 1);
  } else if (target.closest('.variant-picker__form')) {
    // Variant changed: reprice immediately, without waiting for the section fetch.
    nfcSync(target);
  }
});

document.addEventListener('click', (event) => {
  const tier = event.target instanceof Element ? event.target.closest('[data-nfc-tier]') : null;
  if (!tier) return;
  nfcSync(tier, Number(tier.dataset.nfcTier) || 1);
});

const nfcInit = () => {
  document.querySelectorAll('[data-nfc-total]').forEach((el) => nfcSync(el));
};
document.addEventListener('DOMContentLoaded', nfcInit);
document.addEventListener('shopify:section:load', nfcInit);
// Fires once Horizon has swapped in the newly selected variant's markup.
document.addEventListener('shopify:product:select', () => setTimeout(nfcInit, 0));
nfcInit();

// Cart changed (add, quantity edit, line removal): re-evaluate the tier code.
document.addEventListener('shopify:cart:lines-update', () => setTimeout(nfcApplyTierDiscount, 250));
document.addEventListener('DOMContentLoaded', () => setTimeout(nfcApplyTierDiscount, 400));
